import React, { useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { SidebarContext } from '../../contexts/SidebarContext';
import { useAuth } from '../../hooks/useAuth';
import { filterNavigation } from 'helpers/navVisibility';
import navigationConfig from '../../config/navigation';
import SidebarNavGroup from './SidebarNavGroup';
import SidebarNavItem from './SidebarNavItem';
import { brand, colors, palette, size, typography } from 'theme/tokens';

// The Zenvoya wordmark, bundled with this app. It used to be hotlinked from
// app-dev's build output (a content-hashed file), which broke whenever app-dev
// was redeployed or unreachable. Its colour is `currentColor`, set from the
// theme's primary below.

const DRAWER_WIDTH = 260;
const COLLAPSED_WIDTH = 72;

/**
 * Does `pathname` belong to this nav path? Mirrors SidebarNavItem's own NavLink
 * matching (prefix match, except Dashboard's `/admin` which would otherwise
 * prefix-match every admin route).
 */
const pathIsActive = (pathname, navPath) =>
  navPath === '/admin' ? pathname === navPath : pathname === navPath || pathname.startsWith(`${navPath}/`);

/**
 * Which collapsible groups/parents should start OPEN, given the route the page
 * loaded on.
 *
 * Both `SidebarNavGroup`'s per-group `open` state and this component's own
 * `openParents` used to start closed (or hardcoded to one group) regardless of
 * where the user actually was — correct for a client-side navigation, since the
 * sidebar stays mounted and whatever the user last clicked persists, but wrong
 * for a hard reload, which remounts the whole tree and throws that state away.
 * Landing on Tenant Management after a refresh then showed a COLLAPSED Settings
 * group with its own active item hidden inside it.
 *
 * Computed once from `navigationConfig` + the current path, and used only as
 * each state's INITIAL value — a manual collapse afterward, even while still on
 * that route, is not overridden.
 */
const computeInitialState = (pathname) => {
  const openGroups = {};
  // Bookings defaults open on a fresh landing (e.g. straight to /admin) — the
  // pre-existing convention this preserves rather than changes.
  const openParents = { Bookings: true };

  navigationConfig.forEach((group) => {
    const groupMatches = group.items.some((item) =>
      item.children
        ? item.children.some((child) => pathIsActive(pathname, child.path))
        : pathIsActive(pathname, item.path),
    );
    if (group.collapsible && groupMatches) openGroups[group.group] = true;

    group.items.forEach((item) => {
      if (item.children && item.children.some((child) => pathIsActive(pathname, child.path))) {
        openParents[item.label] = true;
      }
    });
  });

  return { openGroups, openParents };
};

export default function Sidebar() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const sidebarCtx = useContext(SidebarContext);
  const { collapsed: pinnedCollapsed, mobileOpen, toggleCollapsed, closeMobile } = sidebarCtx;
  // The mobile drawer is always full width, so the rail look is desktop-only.
  // Only the arrows open or close it — hovering the rail does nothing.
  const collapsed = pinnedCollapsed && !isMobile;
  const { pathname } = useLocation();
  const { perms } = useAuth();
  const [{ openGroups, openParents: initialOpenParents }] = useState(() => computeInitialState(pathname));
  const [openParents, setOpenParents] = useState(initialOpenParents);

  /**
   * The sidebar this role actually gets — groups and parents whose every child
   * is denied are dropped rather than rendered empty.
   *
   * Keyed on `perms.list` rather than `perms`: AuthContext rebuilds the checker
   * object whenever its `value` memo re-runs, so depending on the object itself
   * would refilter the whole tree on unrelated context changes. The permission
   * array is the thing that can actually change the answer, and it only changes
   * on sign-in or a token refresh.
   *
   * `computeInitialState` above deliberately still walks the UNFILTERED config:
   * it only decides which groups start expanded, and a group that gets filtered
   * out never renders to read that flag anyway.
   */
  const visibleNavigation = useMemo(() => filterNavigation(navigationConfig, perms), [perms?.list]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleParent = (label) => {
    setOpenParents((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const drawerWidth = collapsed ? COLLAPSED_WIDTH : DRAWER_WIDTH;

  const drawerContent = (
    <SidebarContext.Provider value={{ ...sidebarCtx, collapsed }}>
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: colors.sidebar.background,
        borderRight: colors.sidebar.edge,
        overflowX: 'hidden',
      }}
    >
      {/* Logo area */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
          px: collapsed ? 1 : 2.5,
          minHeight: 68,
        }}
      >
        {!collapsed && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.625,
                borderRadius: '10px',
                backgroundColor: palette.alpha.white[95],
                alignSelf: 'flex-start',
              }}
            >
              <Box component={brand.Wordmark} role="img" aria-label={brand.name} sx={{ height: size[26], width: 'auto', display: 'block', color: 'primary.main' }} />
            </Box>
            {/* <Typography
              sx={{
                color: '#A1F919',
                fontSize: '0.625rem',
                fontWeight: 700,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                pl: 0.25,
              }}
            >
              Admin & CRM Portal
            </Typography> */}
          </Box>
        )}
        {collapsed && (
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '10px',
              backgroundColor: palette.alpha.white[95],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box component="img" src={brand.logomark} alt={brand.name} sx={{ width: 22, height: 22, objectFit: 'contain' }} />
          </Box>
        )}
        {!isMobile && !collapsed && (
          <Tooltip title="Collapse sidebar" placement="right" arrow>
            <IconButton
              onClick={toggleCollapsed}
              size="small"
              sx={{
                color: 'sidebar.icon',
                bgcolor: (t) => alpha(t.palette.primary.light, 0.1),
                width: (t) => t.spacing(3.5),
                height: (t) => t.spacing(3.5),
                '&:hover': {
                  bgcolor: (t) => alpha(t.palette.primary.light, 0.18),
                  color: 'sidebar.iconHover',
                },
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: (t) => t.spacing(2.25) }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor: colors.sidebar.divider, mx: 2 }} />

      {/* Navigation */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pt: 1.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: colors.sidebar.scrollThumb,
            borderRadius: 2,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: colors.sidebar.scrollThumbHover,
          },
        }}
      >
        <List disablePadding>
          {visibleNavigation.map((group, gi) => (
            <SidebarNavGroup key={group.group || `group-${gi}`} label={group.group} collapsible={group.collapsible} icon={group.icon} defaultOpen={!!openGroups[group.group]}>
              {group.items.map((item) =>
                item.children ? (
                  <React.Fragment key={item.label}>
                    <Tooltip title={collapsed ? item.label : ''} placement="right" arrow>
                      <ListItemButton
                        onClick={() => toggleParent(item.label)}
                        sx={{
                          mx: 1,
                          mb: 0.5,
                          borderRadius: '10px',
                          color: colors.sidebar.text,
                          minHeight: 46,
                          justifyContent: collapsed ? 'center' : 'flex-start',
                          px: collapsed ? 1.5 : 2,
                          transition: 'all 0.15s ease',
                          '&:hover': {
                            backgroundColor: colors.sidebar.hoverBg,
                            color: colors.sidebar.textHover,
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: collapsed ? 0 : 36,
                            color: 'inherit',
                            justifyContent: 'center',
                          }}
                        >
                          <item.icon sx={{ fontSize: typography.fontSize[22] }} />
                        </ListItemIcon>
                        {!collapsed && (
                          <>
                            <ListItemText
                              primary={item.label}
                              primaryTypographyProps={{
                                fontSize: typography.fontSize[14],
                                fontWeight: typography.fontWeight.medium,
                                fontFamily: typography.fontFamily.inter,
                                letterSpacing: '-0.01em',
                              }}
                            />
                            {openParents[item.label] ? (
                              <ExpandLess sx={{ fontSize: typography.fontSize[18], opacity: 0.6 }} />
                            ) : (
                              <ExpandMore sx={{ fontSize: typography.fontSize[18], opacity: 0.6 }} />
                            )}
                          </>
                        )}
                      </ListItemButton>
                    </Tooltip>
                    {!collapsed && (
                      <Collapse in={openParents[item.label]} timeout="auto" unmountOnExit>
                        <List disablePadding sx={{ pl: 1.5 }}>
                          {item.children.map((child) => (
                            <SidebarNavItem
                              key={child.path}
                              label={child.label}
                              path={child.path}
                              icon={child.icon}
                              apiStatus={child.apiStatus}
                              newChat={child.newChat}
                              nested
                            />
                          ))}
                        </List>
                      </Collapse>
                    )}
                  </React.Fragment>
                ) : (
                  <SidebarNavItem
                    key={item.path}
                    label={item.label}
                    path={item.path}
                    icon={item.icon}
                    apiStatus={item.apiStatus}
                  />
                )
              )}
            </SidebarNavGroup>
          ))}
        </List>
      </Box>

      {/* Bottom section */}
      <Divider sx={{ borderColor: colors.sidebar.divider, mx: 2 }} />
      <Box
        sx={{
          p: collapsed ? 1 : 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'space-between',
        }}
      >
        {!collapsed && (
          <Typography
            variant="caption"
            sx={{ color: colors.sidebar.footerText, fontSize: typography.fontSize[14], fontFamily: typography.fontFamily.inter }}
          >
            B2B2C MVP v0.1
          </Typography>
        )}
        {!isMobile && collapsed && (
          <IconButton
            onClick={toggleCollapsed}
            size="small"
            sx={{
              color: 'sidebar.icon',
              bgcolor: (t) => alpha(t.palette.primary.light, 0.1),
              width: (t) => t.spacing(3.5),
              height: (t) => t.spacing(3.5),
              '&:hover': {
                bgcolor: (t) => alpha(t.palette.primary.light, 0.18),
                color: 'sidebar.iconHover',
              },
            }}
          >
            <ChevronRightIcon sx={{ fontSize: (t) => t.spacing(2.25) }} />
          </IconButton>
        )}
      </Box>
    </Box>
    </SidebarContext.Provider>
  );

  if (isMobile) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={closeMobile}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            border: 'none',
            // Square on the left (window edge), theme's 12px kept on the right.
            borderRadius: '0 12px 12px 0',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease-in-out',
        '& .MuiDrawer-paper': {
          width: drawerWidth,
          border: 'none',
          // Square on the left (window edge), theme's 12px kept on the right.
          borderRadius: '0 12px 12px 0',
          transition: 'width 0.2s ease-in-out',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
}

export { DRAWER_WIDTH, COLLAPSED_WIDTH };
